import { AiPill } from '@/components/AiPill';
import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { SegmentedControl } from '@/components/SegmentedControl';
import { errorCopy } from '@/api/errorCopy';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { RecurringIcon, RuleIcon, SearchIcon } from '@/components/icons';
import { Link as RouterLink } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import type { RegisterDivergence } from './BalanceCheckCard';
import { accountsApi } from '@/api/accounts';
import { reimbursementsApi, type ReimbursementLink } from '@/api/reimbursements';
import {
  lineCounterparty,
  sentRegisterQuery,
  transactionsApi,
  type OwnerRegisterLine,
  type OwnerRegisterTotals,
} from '@/api/transactions';
import { transfersApi, type TransferCandidate } from '@/api/transfers';
import { formatCivilDate, formatMoneyMinor, formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { EnvelopeRangeControl } from '@/features/envelope/EnvelopeRangeControl';
import { rangeDates, type EnvelopeRange } from '@/features/envelope/envelopeRange';
import { PayeePrimary } from '@/features/payees/PayeePrimary';
import { isForeignCurrency, isOwnerAccount, canEditAllocations } from './accountAccess';
import { BulkAllocateDialog } from './BulkAllocateDialog';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { ErrorState } from '@/components/ErrorState';
import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { TabularText } from '@/components/TabularText';
import { StatusChip } from '@/components/StatusChip';
import { AllocateDialog } from './AllocateDialog';
import { layerOverSurface, resolveTokens } from '@/theme/theme';
import { PILL_RADIUS } from '@/theme';
import { TEXT_LABEL } from '@/theme/textLabel';
import { useDebouncedText } from '@/shared/useDebouncedText';
import { CounterpartyActions } from './CounterpartyActions';

const PAGE_SIZE = 50;
/**
 * The register is served newest first (TMMAPI-0201): a line to jump to (`?line=`, FIND IT) can sit on
 * an older page, so the first load follows the cursor until it is loaded, at most this many pages.
 */
const SEEK_PAGES = 10;
const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e'] as const;

type PageState =
  | {
      key: string;
      status: 'ready';
      rows: OwnerRegisterLine[];
      nextCursor: string | null;
      totals: OwnerRegisterTotals;
    }
  | { key: string; status: 'error'; error: unknown };

/** The allocation filter: the API takes `unallocatedOnly` or `allocatedOnly`, never both. */
export type RegisterFilter = 'all' | 'unallocated' | 'allocated';

interface OwnerRegisterTabProps {
  account: Account;
  editor: boolean;
  owner: boolean;
  registerFilter: RegisterFilter;
  onRegisterFilter: (filter: RegisterFilter) => void;
  /** The register's window (TMMAPP-0115 A1): the URL's `from` / `to`; both empty = Year to date. */
  range: EnvelopeRange;
  onRangeChange: (range: EnvelopeRange) => void;
  /** The civil today in the owner's zone. */
  today: string;
  openTransactionId: number | null;
  onOpenTransaction: (id: number | null) => void;
  /** A just-created manual line, shown until the next fetch includes it. */
  injectedLine?: OwnerRegisterLine | null;
  reloadToken?: number;
  onNotify: (message: string) => void;
  canImport?: boolean;
  /** Called after classify / clear / link so the parent can refetch the account. */
  onRegisterMutated?: () => void;
  /** FIND IT: the line or date where the file and the ledger part, with the served figures. */
  registerFocus?: RegisterDivergence | null;
  /** Committed register search (`?q=`). Sent only at 2–100 characters, and never for an unmanaged account. */
  searchQuery?: string;
  onSearchQuery?: (value: string) => void;
  /** A line to highlight (`?line=`), from search across accounts. */
  highlightLineId?: number | null;
}

function filterQuery(filter: RegisterFilter): { unallocatedOnly?: true; allocatedOnly?: true } {
  if (filter === 'unallocated') return { unallocatedOnly: true };
  if (filter === 'allocated') return { allocatedOnly: true };
  return {};
}

function selectable(line: OwnerRegisterLine): boolean {
  return line.status === 'POSTED' && line.remainderMinor !== 0 && line.transferAccountId === null;
}

/**
 * Owner register (TMMAPP-0008; TMMAPP-0102, the prototype's 'Bank register'): a card with the
 * All / Unallocated / Allocated filter and the window, the Allocated and After-allocations columns,
 * allocation pills, a totals footer from the served `totals`, a stacked layout below `md`, VOID
 * styling, foreign amounts, Load older lines (served newest first) and the multi-select bulk bar.
 * Row click opens the drawer.
 */
export function OwnerRegisterTab({
  account,
  editor,
  owner,
  registerFilter,
  onRegisterFilter,
  range,
  onRangeChange,
  today,
  openTransactionId,
  onOpenTransaction,
  injectedLine = null,
  reloadToken = 0,
  onNotify,
  canImport = false,
  onRegisterMutated,
  registerFocus = null,
  searchQuery = '',
  onSearchQuery,
  highlightLineId = null,
}: OwnerRegisterTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));

  const tokens = resolveTokens(theme);
  const [tick, setTick] = useState(0);
  const [reimbTick, setReimbTick] = useState(0);
  // "Make it a payee" in the table: one line at a time, in a full-width row under it (item 10).
  const [wireConfirmId, setWireConfirmId] = useState<number | null>(null);
  const [wireConfirmCell, setWireConfirmCell] = useState<HTMLTableCellElement | null>(null);
  const [page, setPage] = useState<PageState | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [candidates, setCandidates] = useState<TransferCandidate[]>([]);
  const [dismissedCandidates, setDismissedCandidates] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const [reimbByTx, setReimbByTx] = useState<Map<number, ReimbursementLink>>(() => new Map());
  const [reimbPeer, setReimbPeer] = useState<
    Map<number, { payee: string; postedOn: string; amountMinor: number; accountId: number }>
  >(() => new Map());
  /** Peer line drawer when the other side is on another account or outside the current window. */
  const [peerDrawer, setPeerDrawer] = useState<{
    account: Account;
    line: OwnerRegisterLine;
  } | null>(null);
  const dates = rangeDates(range, today);
  const from = dates?.from;
  const to = dates?.to;
  // An unmanaged account's register refuses `q` (ACCOUNT_UNMANAGED). A deep link is ignored.
  const searchable = account.management !== 'UNMANAGED';
  const sentQ = searchable ? sentRegisterQuery(searchQuery) : '';
  // A search on the default period (no dates in the URL) looks at all time.
  const allTime = sentQ.length >= 2 && range.from === '' && range.to === '';
  const [searchText, setSearchText] = useDebouncedText(searchQuery, (value) => {
    onSearchQuery?.(value);
  });
  const queryTooLong = searchable && searchText.trim().length > 100;
  const key =
    dates === null
      ? null
      : `${account.id}|${allTime ? '' : (from ?? '')}|${allTime ? '' : (to ?? '')}|${registerFilter}|${sentQ}|${tick}|${reloadToken}`;
  const lastKey = useRef<string | null>(null);
  const seq = useRef(0);
  const foreign = isForeignCurrency(account);
  const operating = account.operatingCurrency ?? account.currency ?? '';
  const bank = account.currency ?? operating;
  const seekId = registerFocus?.transactionId ?? highlightLineId;

  const load = useCallback(
    async (count: number, opts: { throwOnError?: boolean } = {}) => {
      if (key === null) return;
      const mine = ++seq.current;
      try {
        const query = {
          ...(allTime ? {} : { from, to }),
          ...filterQuery(registerFilter),
          ...(sentQ.length >= 2 ? { q: sentQ } : {}),
          limit: PAGE_SIZE,
        };
        let result = await transactionsApi.register(account.id, query);
        const nextRows = [...result.items];
        let pages = 1;
        const seeking = () =>
          seekId !== null && pages < SEEK_PAGES && !nextRows.some((r) => r.id === seekId);
        while ((nextRows.length < count || seeking()) && result.nextCursor !== null) {
          result = await transactionsApi.register(account.id, {
            ...query,
            cursor: result.nextCursor,
          });
          nextRows.push(...result.items);
          pages += 1;
        }
        if (seq.current !== mine) return;
        setPage({
          key,
          status: 'ready',
          rows: nextRows,
          nextCursor: result.nextCursor,
          totals: result.totals,
        });
      } catch (error) {
        if (seq.current !== mine) return;
        setPage({ key, status: 'error', error });
        if (opts.throwOnError) throw error;
      }
    },
    [account.id, from, to, registerFilter, key, allTime, sentQ, seekId],
  );

  useEffect(() => {
    if (key === null || lastKey.current === key) return;
    lastKey.current = key;
    setSelected(new Set());
    void load(0);
  }, [key, load]);

  useEffect(() => {
    let cancelled = false;
    void transfersApi
      .candidates(account.id, 90)
      .then((items) => {
        if (!cancelled) setCandidates(items);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [account.id, tick, reloadToken]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const page = await reimbursementsApi.list('CONFIRMED');
        if (cancelled) return;
        const byTx = new Map<number, ReimbursementLink>();
        for (const link of page.items) {
          byTx.set(link.creditTransactionId, link);
          byTx.set(link.expenseTransactionId, link);
        }
        setReimbByTx(byTx);
        const peer = new Map<
          number,
          { payee: string; postedOn: string; amountMinor: number; accountId: number }
        >();
        for (const link of page.items) {
          const touches =
            link.expenseAccountId === account.id || link.creditAccountId === account.id;
          if (!touches) continue;
          // Only the other side of a line in this register: this account's own side is the row
          // itself, never re-fetched (TMMAPP-0097 B7).
          const sides = [
            ...(link.creditAccountId === account.id
              ? [{ accountId: link.expenseAccountId, txId: link.expenseTransactionId }]
              : []),
            ...(link.expenseAccountId === account.id
              ? [{ accountId: link.creditAccountId, txId: link.creditTransactionId }]
              : []),
          ];
          for (const side of sides) {
            if (side.accountId === null || peer.has(side.txId)) continue;
            try {
              const line = await transactionsApi.get(side.accountId, side.txId);
              peer.set(side.txId, {
                payee: line.payee,
                postedOn: line.postedOn,
                amountMinor: Math.abs(line.operatingAmountMinor),
                accountId: side.accountId,
              });
            } catch {
              /* this linked transaction is unavailable */
            }
          }
        }
        if (!cancelled) setReimbPeer(peer);
      } catch {
        if (!cancelled) {
          setReimbByTx(new Map());
          setReimbPeer(new Map());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account.id, tick, reloadToken, reimbTick]);

  const visibleCandidates = candidates.filter((c) => !dismissedCandidates.has(c.transactionId));
  const ready = page !== null && page.key === key && page.status === 'ready' ? page : null;
  const failed = page !== null && page.key === key && page.status === 'error' ? page : null;
  const loading = key !== null && ready === null && failed === null;
  const fetched = ready?.rows ?? [];
  const rows =
    injectedLine !== null && !fetched.some((r) => r.id === injectedLine.id)
      ? [injectedLine, ...fetched]
      : fetched;
  const balanceFocusId =
    registerFocus === null
      ? null
      : (rows.find((line) =>
          registerFocus.transactionId !== null
            ? line.id === registerFocus.transactionId
            : line.postedOn === registerFocus.postedOn,
        )?.id ?? null);
  const highlightId =
    highlightLineId !== null && rows.some((line) => line.id === highlightLineId)
      ? highlightLineId
      : null;
  const focusLineId = balanceFocusId ?? highlightId;
  const focusNode = useRef<HTMLElement | null>(null);
  const setFocusNode = (node: HTMLElement | null) => {
    focusNode.current = node;
  };
  useEffect(() => {
    const node = focusNode.current;
    if (focusLineId === null || node === null) return;
    if (typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'center' });
  }, [focusLineId]);
  const lineFocused = (line: OwnerRegisterLine) =>
    (registerFocus !== null &&
      (registerFocus.transactionId !== null
        ? line.id === registerFocus.transactionId
        : line.postedOn === registerFocus.postedOn)) ||
    line.id === highlightId;
  const openLine =
    openTransactionId === null
      ? null
      : (rows.find((r) => r.id === openTransactionId) ??
        (injectedLine !== null && injectedLine.id === openTransactionId ? injectedLine : null));

  async function handleLoadMore() {
    if (ready === null || ready.nextCursor === null || loadingMore) return;
    const startKey = ready.key;
    setLoadingMore(true);
    try {
      const next = await transactionsApi.register(account.id, {
        ...(allTime ? {} : { from, to }),
        ...filterQuery(registerFilter),
        ...(sentQ.length >= 2 ? { q: sentQ } : {}),
        cursor: ready.nextCursor,
        limit: PAGE_SIZE,
      });
      setPage((prev) =>
        prev !== null && prev.status === 'ready' && prev.key === startKey
          ? {
              ...prev,
              rows: [...prev.rows, ...next.items],
              nextCursor: next.nextCursor,
              totals: next.totals,
            }
          : prev,
      );
    } catch {
      onNotify(t('accounts.errorGeneric'));
    } finally {
      setLoadingMore(false);
    }
  }

  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const selectedLines = rows.filter((r) => selected.has(r.id));
  const selectedSign =
    selectedLines.length > 0 ? Math.sign(selectedLines[0]!.remainderMinor) : null;
  const selectableRows = rows.filter(
    (r) => selectable(r) && (selectedSign === null || Math.sign(r.remainderMinor) === selectedSign),
  );
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.id));

  async function afterWrite(_updated: OwnerRegisterLine) {
    lastKey.current = null;
    await load(rows.length, { throwOnError: true });
    // Refresh reimbursement chips without bumping `tick` (that remounts the register /
    // drawer and races Escape in e2e). Transfer candidates are refreshed explicitly below.
    setReimbTick((n) => n + 1);
    void transfersApi
      .candidates(account.id, 90)
      .then(setCandidates)
      .catch(() => undefined);
    onRegisterMutated?.();
  }

  function handleWireChanged() {
    lastKey.current = null;
    setTick((n) => n + 1);
    onRegisterMutated?.();
  }

  async function openReimbPeer(peerTxId: number, peerAccountId: number) {
    try {
      const [peerAccount, peerLine] = await Promise.all([
        peerAccountId === account.id ? Promise.resolve(account) : accountsApi.get(peerAccountId),
        transactionsApi.get(peerAccountId, peerTxId),
      ]);
      const inCurrentRegister =
        peerAccount.id === account.id &&
        (rows.some((r) => r.id === peerLine.id) ||
          (injectedLine !== null && injectedLine.id === peerLine.id));
      if (inCurrentRegister) {
        setPeerDrawer(null);
        onOpenTransaction(peerLine.id);
        return;
      }
      // Other account, or same account but outside the loaded window.
      onOpenTransaction(null);
      setPeerDrawer({ account: peerAccount, line: peerLine });
    } catch {
      onNotify(t('accounts.errorGeneric'));
    }
  }

  async function handleCandidateYes(candidate: TransferCandidate) {
    try {
      await transfersApi.link(candidate.transactionId, candidate.counterpartyTransactionId);
      lastKey.current = null;
      await load(rows.length);
      const items = await transfersApi.candidates(account.id, 90);
      setCandidates(items);
      onRegisterMutated?.();
    } catch (error) {
      onNotify(errorCopy(t, error, 'accounts.errorGeneric'));
    }
  }

  function handleCandidateNo(candidate: TransferCandidate) {
    setDismissedCandidates((prev) => new Set(prev).add(candidate.transactionId));
  }

  const footerFilter = t(`accounts.registerFooterFilter.${registerFilter}`);
  const totals = ready?.totals ?? null;
  const countShown = totals?.count ?? rows.length;
  const countAll = totals?.allCount ?? countShown;
  const activeFilters =
    (range.from !== '' || range.to !== '' ? 1 : 0) +
    (registerFilter !== 'all' ? 1 : 0) +
    (sentQ.length >= 2 ? 1 : 0);
  const matchCount = totals?.count ?? rows.length;
  const footCellSx = { borderTop: `2px solid ${tokens.line}`, borderBottom: 0 } as const;

  /** "Sep 15" (P:505): the year only when it isn't this year's. */
  function shortDate(civil: string): string {
    return formatCivilDate(
      civil,
      locale,
      civil.slice(0, 4) === today.slice(0, 4)
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' },
    );
  }

  /**
   * The source line (§E 15): "bank · batch #{n}", "bank · transfer", "rule · {name}" or "manual" (the
   * other sources by their label), with the operating equivalent before it on a foreign line.
   */
  function sourceText(line: OwnerRegisterLine): string {
    if (line.transferAccountId !== null) return t('accounts.sourceLine.transfer');
    const rule = line.allocations.find(
      (a) => a.origin === 'RULE' && a.ruleName !== null && a.ruleName.length > 0,
    );
    if (rule !== undefined) return t('accounts.sourceLine.rule', { name: rule.ruleName });
    switch (line.source) {
      case 'BANK_FILE':
        return line.importBatchId !== null
          ? t('accounts.sourceLine.batch', { id: line.importBatchId })
          : t('accounts.sourceLine.bank');
      case 'MANUAL':
        return t('accounts.sourceLine.manual');
      default:
        return t(`accounts.source.${line.source}`);
    }
  }

  /** The source line's text: a foreign line's "≈ {operating}" first, then the source. */
  function sourceLineText(line: OwnerRegisterLine): string {
    const operatingEquivalent =
      foreign && line.currency !== operating
        ? t('accounts.sourceLine.operating', {
            amount: formatMoneyMinor(line.operatingAmountMinor, operating, locale, true),
          })
        : null;
    return [operatingEquivalent, sourceText(line)].filter(Boolean).join(' · ');
  }

  function sourceLine(line: OwnerRegisterLine) {
    const text = sourceLineText(line);
    const body = (
      <Box
        component="span"
        data-source-line=""
        sx={{
          display: 'block',
          fontSize: 12,
          lineHeight: '17px',
          color: tokens.textLabel('surface'),
        }}
      >
        {text}
      </Box>
    );
    return line.source === 'CONNECTOR' ? (
      <Tooltip describeChild title={t('connections.originTooltip')}>
        {body}
      </Tooltip>
    ) : (
      body
    );
  }

  /** An allocation chip's shape (visual contract v2 §8): a 3 × 9 pill, 12 px. */
  const chipSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    minHeight: 0,
    p: '3px 9px',
    borderRadius: `${PILL_RADIUS}px`,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
  } as const;

  /**
   * The envelopes a line went to (§E 15): "{envelope} {±amount}" chips, each opening its envelope
   * (`/v/{va}/e/{env}`; an Unassigned part its virtual account); "Unallocated" as the dashed chip
   * (with what's left when part of it is allocated), which opens the allocate dialog. A transfer
   * and a reimbursement keep their own chips. Allocations are in the operating currency (MM-D-63).
   */
  function envelopeChips(line: OwnerRegisterLine) {
    if (line.transferAccountId !== null) {
      return (
        <Box onClick={(event) => event.stopPropagation()}>
          <PairedTransferChip line={line} operatingCurrency={operating} locale={locale} />
        </Box>
      );
    }
    const reimb = reimbByTx.get(line.id);
    const creditSide = reimb !== undefined && reimb.creditTransactionId === line.id;
    const unallocated =
      line.status !== 'VOID' && line.remainderMinor !== 0 && !creditSide && reimb === undefined;
    const partial = line.allocations.length > 0;
    return (
      <Stack
        direction="row"
        data-envelope-chips=""
        sx={{ gap: '6px', flexWrap: 'wrap' }}
        onClick={(event) => event.stopPropagation()}
      >
        {reimb !== undefined && (
          <ReimbursementRegisterChip
            line={line}
            link={reimb}
            peer={
              reimbPeer.get(
                reimb.creditTransactionId === line.id
                  ? reimb.expenseTransactionId
                  : reimb.creditTransactionId,
              ) ?? null
            }
            operatingCurrency={operating}
            locale={locale}
            onOpenPeer={(peerTxId, peerAccountId) => {
              void openReimbPeer(peerTxId, peerAccountId);
            }}
          />
        )}
        {!creditSide &&
          line.allocations.map((alloc) => {
            const label = alloc.unassigned
              ? t('virtualAccount.unassigned')
              : (alloc.envelopeName ?? t('accounts.envelopeHidden'));
            const to =
              alloc.virtualAccountId === null
                ? null
                : alloc.envelopeId !== null && !alloc.unassigned
                  ? `/v/${alloc.virtualAccountId}/e/${alloc.envelopeId}`
                  : `/v/${alloc.virtualAccountId}`;
            const content = (
              <>
                {label} <MoneyParts minor={alloc.amountMinor} currency={operating} signed />
              </>
            );
            const look = {
              ...chipSx,
              // Contract v2 §8's `successText` chip, on its guaranteed wash: `successSoft` is the
              // prototype's sage, and `successText` on `brandSoft` isn't a saas-auth pair.
              bgcolor: tokens.successSoft,
              border: `1px solid ${tokens.line}`,
              color: tokens.successText,
            };
            return to === null ? (
              <Box key={alloc.id} component="span" data-allocation-chip="" sx={look}>
                {content}
              </Box>
            ) : (
              <Box
                key={alloc.id}
                component={RouterLink}
                to={to}
                data-allocation-chip=""
                sx={{ ...look, '&:hover': { borderColor: tokens.brandBoundary } }}
              >
                {content}
              </Box>
            );
          })}
        {unallocated && (
          <Box
            // An editor allocates from it; anyone else reads it.
            {...(editor
              ? {
                  component: 'button' as const,
                  type: 'button' as const,
                  'aria-label': t('accounts.allocateLine', { payee: line.payee }),
                  onClick: () => onOpenTransaction(line.id),
                }
              : { component: 'span' as const })}
            data-unallocated-chip=""
            sx={{
              ...chipSx,
              fontFamily: 'inherit',
              cursor: 'pointer',
              bgcolor: 'transparent',
              border: `1px dashed ${tokens.lineStrong}`,
              color: tokens.text2,
              '&:hover': { borderColor: tokens.brandBoundary },
            }}
          >
            {partial ? (
              <>
                {t('accounts.unallocatedChip')}{' '}
                <MoneyParts minor={line.remainderMinor} currency={operating} signed />
              </>
            ) : (
              t('accounts.unallocatedChip')
            )}
          </Box>
        )}
      </Stack>
    );
  }

  const toolbar = (
    <Stack
      direction="row"
      sx={{
        gap: 1.5,
        // One row from `md` up, as the prototype draws it; stacked below.
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      {searchable && (
        <TextField
          size="small"
          type="search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t('search.placeholder')}
          helperText={queryTooLong ? t('search.tooLong') : undefined}
          slotProps={{
            htmlInput: { 'aria-label': t('search.placeholder') },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" aria-hidden sx={{ display: 'flex', color: tokens.text3 }}>
                    <SearchIcon />
                  </Box>
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: '1 1 220px', minWidth: 0, maxWidth: { md: 360 } }}
        />
      )}
      <SegmentedControl<RegisterFilter>
        filter
        aria-label={t('accounts.registerFilterLabel')}
        value={registerFilter}
        options={[
          { value: 'all', label: t('accounts.registerFilter.all') },
          { value: 'unallocated', label: t('accounts.registerFilter.unallocated') },
          { value: 'allocated', label: t('accounts.registerFilter.allocated') },
        ]}
        onChange={onRegisterFilter}
      />
      <ClearFiltersButton
        active={activeFilters}
        onClear={() => {
          onRangeChange({ from: '', to: '' });
          onRegisterFilter('all');
          onSearchQuery?.('');
        }}
      />
      {canImport && (
        <Button
          component={RouterLink}
          to={`/me/accounts/${account.id}/import`}
          variant="outlined"
          // One line, never "New / import" in a tight toolbar (TMMAPP-0155 item 14).
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {t('imports.new')}
        </Button>
      )}
    </Stack>
  );

  const leadSpan = (editor ? 1 : 0) + 2;
  const columnCount = leadSpan + 2;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {visibleCandidates.length > 0 && (
        <Paper
          role="group"
          aria-label={t('transfer.candidatesTitle', { count: visibleCandidates.length })}
        >
          <Box sx={{ p: '16px 20px', borderBottom: `1px solid ${tokens.line}` }}>
            <Typography variant="h3" component="h2">
              {t('transfer.candidatesTitle', { count: visibleCandidates.length })}
            </Typography>
            <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
              {t('transfer.candidatesNote')}
            </Typography>
          </Box>
          {md ? (
            <TableContainer>
              <Table
                size="small"
                aria-label={t('transfer.candidatesTitle', { count: visibleCandidates.length })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('accounts.colDate')}</TableCell>
                    <TableCell>{t('accounts.colPayee')}</TableCell>
                    <TableCell align="right">{t('accounts.colAmount')}</TableCell>
                    <TableCell>{t('transfer.account')}</TableCell>
                    <TableCell>{t('accounts.colDate')}</TableCell>
                    <TableCell align="right">{t('accounts.colAmount')}</TableCell>
                    <ActionsHeaderCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleCandidates.map((c) => (
                    <TableRow key={`${c.transactionId}-${c.counterpartyTransactionId}`}>
                      <TableCell>
                        <TabularText>{formatCivilDate(c.postedOn, locale)}</TabularText>
                      </TableCell>
                      <TableCell>{c.payee}</TableCell>
                      <TableCell align="right">
                        <MoneyText
                          minor={c.operatingAmountMinor}
                          currency={c.currency || operating}
                        />
                      </TableCell>
                      <TableCell>{c.counterpartyAccountName}</TableCell>
                      <TableCell>
                        <TabularText>{formatCivilDate(c.counterpartyPostedOn, locale)}</TabularText>
                      </TableCell>
                      <TableCell align="right">
                        <MoneyText
                          minor={c.counterpartyOperatingAmountMinor}
                          currency={c.currency || operating}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => void handleCandidateYes(c)}
                          >
                            {t('transfer.yes')}
                          </Button>
                          <Button size="small" onClick={() => handleCandidateNo(c)}>
                            {t('transfer.no')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Stack spacing={1} sx={{ p: 2 }}>
              {visibleCandidates.map((c) => (
                <Paper
                  key={`${c.transactionId}-${c.counterpartyTransactionId}`}
                  variant="nested"
                  sx={{ p: 1.5 }}
                >
                  <Stack spacing={0.75}>
                    <Typography variant="body2">
                      {formatCivilDate(c.postedOn, locale)} · {c.payee} ·{' '}
                      <MoneyText
                        minor={c.operatingAmountMinor}
                        currency={c.currency || operating}
                      />
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      ↔ {c.counterpartyAccountName} ·{' '}
                      {formatCivilDate(c.counterpartyPostedOn, locale)} ·{' '}
                      <MoneyText
                        minor={c.counterpartyOperatingAmountMinor}
                        currency={c.currency || operating}
                      />
                    </Typography>
                    <Stack direction="row" sx={{ gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => void handleCandidateYes(c)}
                      >
                        {t('transfer.yes')}
                      </Button>
                      <Button size="small" onClick={() => handleCandidateNo(c)}>
                        {t('transfer.no')}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {failed !== null && (
        <ErrorState
          variant="section"
          error={failed.error}
          what={t('errors.what.accountRegister')}
          onRetry={() => setTick((n) => n + 1)}
        />
      )}

      {failed === null && (
        <Paper sx={{ p: 0, overflow: 'hidden' }} data-register-card="">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            data-card-header=""
            sx={{
              alignItems: { md: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              p: '16px 20px',
              borderBottom: `1px solid ${tokens.line}`,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" component="h2">
                {t('accounts.registerTitle')}
              </Typography>
              <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
                {t('accounts.registerSub')}
              </Typography>
            </Box>
            {toolbar}
          </Stack>
          {/* The window's chips get their own row under the header, as the envelope register's do
              (TMMAPP-0115 A1): one range vocabulary everywhere. */}
          <Box
            data-register-range=""
            sx={{ p: '12px 20px', borderBottom: `1px solid ${tokens.line}` }}
          >
            <EnvelopeRangeControl
              value={range}
              onChange={onRangeChange}
              today={today}
              allTime={allTime}
            />
          </Box>

          {registerFocus !== null && (
            <Typography
              id="balance-part-note"
              variant="body2"
              role="status"
              sx={{ p: '12px 20px' }}
            >
              <Trans
                i18nKey="accounts.balanceCheck.partHere"
                components={{
                  file: (
                    <MoneyText minor={registerFocus.fileMinor} currency={bank} variant="body2" />
                  ),
                  ledger: (
                    <MoneyText minor={registerFocus.ledgerMinor} currency={bank} variant="body2" />
                  ),
                }}
              />
            </Typography>
          )}

          {loading && (
            <Stack spacing={1} aria-busy="true" sx={{ p: 2 }}>
              {SKELETON_KEYS.map((k) => (
                <Skeleton key={k} variant="rounded" height={36} />
              ))}
            </Stack>
          )}

          {ready !== null && rows.length === 0 && (
            <Stack spacing={1} sx={{ p: '16px 20px' }}>
              <Typography variant="body1">
                {sentQ.length >= 2 ? t('search.empty', { q: sentQ }) : t('accounts.registerEmpty')}
              </Typography>
              {sentQ.length >= 2 && (
                <Box>
                  <Button onClick={() => onSearchQuery?.('')}>{t('search.clear')}</Button>
                </Box>
              )}
            </Stack>
          )}

          {ready !== null && rows.length > 0 && md && (
            <Box
              tabIndex={0}
              role="region"
              aria-label={t('accounts.registerTitle')}
              sx={{
                overflowX: 'auto',
                // The Make it a payee row sizes to the visible width, not the table's (item 10).
                containerType: 'inline-size',
                '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: -2 },
              }}
            >
              <Table
                size="small"
                aria-label={t('accounts.tabRegister')}
                data-register-v2=""
                sx={{
                  minWidth: 640,
                  '& .MuiTableCell-root': { borderBottom: 'none' },
                  '& .MuiTableBody-root .MuiTableCell-root': {
                    borderTop: `1px solid ${tokens.line}`,
                    p: '13px 9px',
                    verticalAlign: 'top',
                  },
                  '& .MuiTableCell-root:first-of-type': { paddingInlineStart: '20px' },
                  '& .MuiTableCell-root:last-of-type': { paddingInlineEnd: '20px' },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: tokens.surface2,
                      [TEXT_LABEL]: tokens.textLabel('surface2'),
                      '& .MuiTableCell-root': {
                        p: '12px 9px',
                        fontSize: 11.5,
                        lineHeight: '16px',
                        fontWeight: 400,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: tokens.textLabel('surface2'),
                      },
                    }}
                  >
                    {editor && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={!allSelected && selected.size > 0}
                          onChange={() => {
                            if (allSelected) {
                              setSelected(new Set());
                              return;
                            }
                            const sign =
                              selectedSign ?? Math.sign(rows.find(selectable)?.remainderMinor ?? 0);
                            setSelected(
                              new Set(
                                rows
                                  .filter(
                                    (r) => selectable(r) && Math.sign(r.remainderMinor) === sign,
                                  )
                                  .map((r) => r.id),
                              ),
                            );
                          }}
                          slotProps={{ input: { 'aria-label': t('accounts.selectAll') } }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ width: 62 }}>{t('accounts.colDate')}</TableCell>
                    <TableCell>{t('accounts.colDescriptionEnvelopes')}</TableCell>
                    <TableCell align="right">{t('accounts.colAmount')}</TableCell>
                    <TableCell align="right" sx={{ minWidth: 96 }}>
                      {t('accounts.colRegisterBalance')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((line) => {
                    const voided = line.status === 'VOID';
                    const counterparty = lineCounterparty(line);
                    const beforeOpening = line.beforeOpening === true;
                    const focused = lineFocused(line);
                    const ruleAlloc = line.allocations.find((a) => a.origin === 'RULE');
                    const ruleTooltip =
                      ruleAlloc !== undefined &&
                      ruleAlloc.ruleName !== null &&
                      ruleAlloc.ruleName.length > 0
                        ? t('rules.originTooltip', { name: ruleAlloc.ruleName })
                        : t('envelope.origin.RULE');
                    return (
                      <Fragment key={line.id}>
                        <TableRow
                          hover={!focused}
                          data-balance-part={focused ? '' : undefined}
                          aria-current={focused ? 'true' : undefined}
                          aria-describedby={focused ? 'balance-part-note' : undefined}
                          ref={line.id === focusLineId ? setFocusNode : undefined}
                          onClick={() => onOpenTransaction(line.id)}
                          sx={{
                            cursor: 'pointer',
                            ...(focused
                              ? {
                                  background: layerOverSurface(tokens, tokens.selected),
                                  outline: `2px solid ${tokens.focusRing}`,
                                  outlineOffset: -2,
                                }
                              : {}),
                            // A voided line is still read: text2, including money (not the money role).
                            ...(voided
                              ? {
                                  color: tokens.text2,
                                  '& .MuiTableCell-root': { color: tokens.text2 },
                                  '& .MuiTableCell-root .MuiTypography-root': {
                                    color: tokens.text2,
                                  },
                                }
                              : beforeOpening
                                ? {
                                    color: tokens.text2,
                                    '& .MuiTableCell-root:not([data-money])': {
                                      color: tokens.text2,
                                    },
                                    '& .MuiTableCell-root:not([data-money]) .MuiTypography-root': {
                                      color: tokens.text2,
                                    },
                                  }
                                : {}),
                          }}
                        >
                          {editor && (
                            <TableCell
                              padding="checkbox"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {selectable(line) &&
                                (selectedSign === null ||
                                  Math.sign(line.remainderMinor) === selectedSign) && (
                                  <Checkbox
                                    checked={selected.has(line.id)}
                                    onChange={() => toggleSelected(line.id)}
                                    slotProps={{ input: { 'aria-label': t('accounts.selectRow') } }}
                                  />
                                )}
                            </TableCell>
                          )}
                          <TableCell
                            sx={{ color: tokens.text2, whiteSpace: 'nowrap', fontSize: 13 }}
                          >
                            <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
                              {beforeOpening ? (
                                <Tooltip describeChild title={t('accounts.beforeOpeningTooltip')}>
                                  <span>{shortDate(line.postedOn)}</span>
                                </Tooltip>
                              ) : (
                                <span>{shortDate(line.postedOn)}</span>
                              )}
                              {line.plannedOccurrence !== null && (
                                <Tooltip
                                  title={t('accounts.matchedPlanned', {
                                    payee: line.plannedOccurrence.payee,
                                    date: formatCivilDate(
                                      line.plannedOccurrence.originalOn,
                                      locale,
                                    ),
                                  })}
                                >
                                  <Box
                                    component="span"
                                    role="img"
                                    aria-label={t('accounts.matchedPlanned', {
                                      payee: line.plannedOccurrence.payee,
                                      date: formatCivilDate(
                                        line.plannedOccurrence.originalOn,
                                        locale,
                                      ),
                                    })}
                                    sx={{ display: 'inline-flex' }}
                                  >
                                    <RecurringIcon />
                                  </Box>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <Stack sx={{ gap: '6px', minWidth: 0 }}>
                              <Stack
                                direction="row"
                                sx={{ gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}
                              >
                                <PayeePrimary
                                  bankText={line.payee}
                                  payeeRef={line.payeeRef}
                                  struck={voided}
                                  nameAdornment={
                                    line.payeeSource === 'AI' ? (
                                      <AiPill compact describes="payeeName" />
                                    ) : undefined
                                  }
                                />
                                {voided && <StatusChip domain="transaction" status="VOID" />}
                                {line.source === 'ADJUSTMENT' && <AdjustmentTag />}
                                {line.zelle !== undefined && <ZelleTag />}
                                {ruleAlloc !== undefined && (
                                  <Tooltip
                                    title={
                                      isOwnerAccount(account) ? (
                                        <MuiLink
                                          component={RouterLink}
                                          to={`/me/accounts/${account.id}?tab=rules`}
                                          underline="always"
                                          color="inherit"
                                        >
                                          {ruleTooltip}
                                        </MuiLink>
                                      ) : (
                                        ruleTooltip
                                      )
                                    }
                                  >
                                    <Box
                                      component="span"
                                      role="img"
                                      aria-label={ruleTooltip}
                                      sx={{ display: 'inline-flex', color: 'text.secondary' }}
                                    >
                                      <RuleIcon />
                                    </Box>
                                  </Tooltip>
                                )}
                              </Stack>
                              {counterparty !== null && (
                                <CounterpartyActions
                                  account={account}
                                  transactionId={line.id}
                                  counterparty={counterparty}
                                  operatingCurrency={operating}
                                  onNotify={onNotify}
                                  onChanged={handleWireChanged}
                                  confirmOpen={wireConfirmId === line.id}
                                  onConfirmOpenChange={(open) =>
                                    setWireConfirmId((current) =>
                                      open ? line.id : current === line.id ? null : current,
                                    )
                                  }
                                  confirmContainer={
                                    wireConfirmId === line.id ? wireConfirmCell : null
                                  }
                                />
                              )}
                              {line.memo !== null && line.memo.length > 0 && (
                                <Typography
                                  variant="caption"
                                  component="div"
                                  title={line.memo}
                                  sx={{
                                    color: tokens.text2,
                                    fontSize: 12,
                                    // One line with an ellipsis; the full text stays in the DOM, so
                                    // it is the accessible name.
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {line.memo}
                                </Typography>
                              )}
                              {sourceLine(line)}
                              {envelopeChips(line)}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" data-money="">
                            <Tooltip
                              describeChild
                              title={
                                foreign && line.fxRate !== null
                                  ? t('accounts.fxRate', {
                                      rate: formatNumber(line.fxRate, locale, {
                                        maximumFractionDigits: 6,
                                      }),
                                    })
                                  : ''
                              }
                            >
                              <Box
                                component="span"
                                sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}
                              >
                                <MoneyText
                                  minor={line.amountMinor}
                                  signed
                                  currency={line.currency || bank}
                                  struck={voided}
                                  tone="register"
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" data-money="" sx={{ minWidth: 96 }}>
                            <MoneyText
                              minor={line.runningBalanceMinor}
                              currency={bank}
                              struck={voided}
                              tone="running"
                            />
                          </TableCell>
                        </TableRow>
                        {wireConfirmId === line.id && counterparty !== null && (
                          <TableRow data-wire-confirm-row="">
                            <TableCell colSpan={columnCount} ref={setWireConfirmCell} />
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow data-register-footer="">
                    <TableCell
                      colSpan={leadSpan}
                      sx={{ ...footCellSx, color: tokens.textLabel('surface'), fontSize: 12 }}
                    >
                      <Box component="span" sx={{ display: 'block' }}>
                        {sentQ.length >= 2
                          ? t('search.matches', { shown: rows.length, count: matchCount })
                          : t('accounts.registerCount', {
                              shown: countShown,
                              count: countAll,
                              filter: footerFilter,
                            })}
                      </Box>
                      {totals !== null && (
                        <Box
                          component="span"
                          data-register-allocated-totals=""
                          sx={{ display: 'block' }}
                        >
                          {totals.afterAllocationsMinor != null
                            ? t('accounts.registerAllocatedTotals', {
                                allocated: formatMoneyMinor(
                                  totals.allocatedMinor,
                                  operating,
                                  locale,
                                ),
                                after: formatMoneyMinor(
                                  totals.afterAllocationsMinor,
                                  operating,
                                  locale,
                                ),
                              })
                            : t('accounts.registerAllocatedTotal', {
                                allocated: formatMoneyMinor(
                                  totals.allocatedMinor,
                                  operating,
                                  locale,
                                ),
                              })}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={footCellSx}>
                      {totals !== null && (
                        <Box sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>
                          <MoneyText
                            minor={totals.netMinor}
                            currency={totals.currency}
                            tone="total"
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={footCellSx} />
                  </TableRow>
                </TableFooter>
              </Table>
            </Box>
          )}

          {ready !== null && rows.length > 0 && !md && (
            <Box
              component="ul"
              aria-label={t('accounts.tabRegister')}
              sx={{ m: 0, p: 0, listStyle: 'none' }}
            >
              {rows.map((line) => {
                const voided = line.status === 'VOID';
                const counterparty = lineCounterparty(line);
                const focused = lineFocused(line);
                const rowText = {
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  fontSize: 12,
                  color: tokens.text2,
                } as const;
                return (
                  <Box component="li" key={line.id} sx={{ display: 'block' }}>
                    <Box
                      component="button"
                      type="button"
                      data-balance-part={focused ? '' : undefined}
                      aria-current={focused ? 'true' : undefined}
                      aria-describedby={focused ? 'balance-part-note' : undefined}
                      ref={line.id === focusLineId ? setFocusNode : undefined}
                      onClick={() => onOpenTransaction(line.id)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        width: '100%',
                        textAlign: 'start',
                        font: 'inherit',
                        color: voided ? tokens.text2 : tokens.text,
                        background: focused
                          ? layerOverSurface(tokens, tokens.selected)
                          : 'transparent',
                        border: 'none',
                        borderBottom: `1px solid ${tokens.line}`,
                        p: '14px 16px',
                        cursor: 'pointer',
                        '&:focus-visible': {
                          outline: `2px solid ${tokens.focusRing}`,
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <Box
                        component="span"
                        data-narrow-first=""
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 1.5,
                          alignItems: 'baseline',
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 13, color: tokens.text2 }}>
                          {shortDate(line.postedOn)}
                        </Box>
                        <Box component="span" sx={{ '& .MuiTypography-root': { fontWeight: 600 } }}>
                          <MoneyText
                            minor={line.amountMinor}
                            signed
                            currency={line.currency || bank}
                            struck={voided}
                            tone="register"
                          />
                        </Box>
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          minWidth: 0,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {line.payeeRef?.name ?? line.payee}
                        {line.source === 'ADJUSTMENT' && (
                          <Box
                            component="span"
                            sx={{ display: 'inline-flex', marginInlineStart: 0.75 }}
                          >
                            <AdjustmentTag />
                          </Box>
                        )}
                        {line.zelle !== undefined && (
                          <Box
                            component="span"
                            sx={{ display: 'inline-flex', marginInlineStart: 0.75 }}
                          >
                            <ZelleTag />
                          </Box>
                        )}
                        {line.payeeSource === 'AI' && (
                          <Box
                            component="span"
                            sx={{ display: 'inline-flex', marginInlineStart: 0.75 }}
                          >
                            <AiPill compact describes="payeeName" />
                          </Box>
                        )}
                      </Box>
                      <Box component="span" sx={rowText}>
                        <span data-source-line="">{sourceLineText(line)}</span>
                        <span>
                          {t('accounts.narrowBank', {
                            amount:
                              line.runningBalanceMinor === null
                                ? t('common.notAvailable')
                                : formatMoneyMinor(line.runningBalanceMinor, bank, locale),
                          })}
                        </span>
                      </Box>
                    </Box>
                    <Box sx={{ px: 2, pb: 1.5, pt: 0.25 }}>{envelopeChips(line)}</Box>
                    {counterparty !== null && (
                      <Box sx={{ px: 2, pb: 1.5 }}>
                        <CounterpartyActions
                          account={account}
                          transactionId={line.id}
                          counterparty={counterparty}
                          operatingCurrency={operating}
                          onNotify={onNotify}
                          onChanged={handleWireChanged}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
              <Box
                component="li"
                sx={{
                  display: 'block',
                  p: '14px 16px',
                  fontSize: 12,
                  color: tokens.textLabel('surface'),
                }}
              >
                {sentQ.length >= 2
                  ? t('search.matches', { shown: rows.length, count: matchCount })
                  : t('accounts.registerCount', {
                      shown: countShown,
                      count: countAll,
                      filter: footerFilter,
                    })}
              </Box>
            </Box>
          )}

          {ready !== null && ready.nextCursor !== null && (
            <Stack
              direction="row"
              sx={{ justifyContent: 'center', p: 2, borderTop: `1px solid ${tokens.line}` }}
            >
              <Button
                variant="outlined"
                onClick={() => void handleLoadMore()}
                loading={loadingMore}
              >
                {t('common.loadOlderLines')}
              </Button>
            </Stack>
          )}
        </Paper>
      )}

      {editor && selected.size > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            bottom: 16,
            p: 1.5,
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2">
            {t('accounts.selectedCount', { count: selected.size })}
          </Typography>
          <Button variant="contained" onClick={() => setBulkOpen(true)}>
            {t('accounts.bulkAllocate')}
          </Button>
        </Paper>
      )}

      {openLine !== null && peerDrawer === null && (
        <AllocateDialog
          account={account}
          line={openLine}
          editor={editor}
          owner={owner}
          onClose={() => onOpenTransaction(null)}
          onChanged={afterWrite}
          onNotify={onNotify}
        />
      )}

      {peerDrawer !== null && (
        <AllocateDialog
          account={peerDrawer.account}
          line={peerDrawer.line}
          editor={canEditAllocations(peerDrawer.account)}
          owner={isOwnerAccount(peerDrawer.account)}
          onClose={() => setPeerDrawer(null)}
          onChanged={async (updated) => {
            setPeerDrawer((prev) => (prev !== null ? { ...prev, line: updated } : prev));
            if (peerDrawer.account.id === account.id) {
              lastKey.current = null;
              await load(rows.length);
            }
            onRegisterMutated?.();
          }}
          onNotify={onNotify}
        />
      )}

      {bulkOpen && (
        <BulkAllocateDialog
          lines={selectedLines}
          operatingCurrency={operating}
          onClose={() => setBulkOpen(false)}
          onDone={async () => {
            setSelected(new Set());
            lastKey.current = null;
            await load(rows.length);
          }}
        />
      )}
    </Box>
  );
}

/** The register's ADJUSTMENT mark (MM-D-144): the line counts in the balance and stays out of reports. */
function AdjustmentTag() {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Tooltip describeChild title={t('accounts.balanceCheck.tagTooltip')}>
      <Typography variant="micro" component="span" sx={{ color: tokens.textLabel('bg') }}>
        {t('accounts.balanceCheck.tag')}
      </Typography>
    </Tooltip>
  );
}

/** The register's Zelle mark beside the payee (TMMAPP-0136 item 8; the facts box names the counterparty). */
function ZelleTag() {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Typography variant="micro" component="span" sx={{ color: tokens.textLabel('bg') }}>
      {t('wire.zelleTag')}
    </Typography>
  );
}

/**
 * Transfer chip with paired tooltip using the counterparty's date and amount
 * (YAML `counterpartAmountMinor`; date fetched from the pair line — no YAML date field).
 */
function PairedTransferChip({
  line,
  operatingCurrency,
  locale,
}: {
  line: OwnerRegisterLine;
  operatingCurrency: string | null;
  locale: string;
}) {
  const { t } = useTranslation();
  const [pairPostedOn, setPairPostedOn] = useState<string | null>(null);
  const [pairAmountMinor, setPairAmountMinor] = useState<number | null>(null);

  useEffect(() => {
    const accountId = line.transferAccountId;
    const pairId = line.transferPairTransactionId;
    if (accountId === null || pairId === null) return;
    let cancelled = false;
    void transactionsApi
      .get(accountId, pairId)
      .then((pair) => {
        if (cancelled) return;
        setPairPostedOn(pair.postedOn);
        setPairAmountMinor(pair.operatingAmountMinor);
      })
      .catch(() => {
        if (!cancelled) {
          setPairPostedOn(null);
          setPairAmountMinor(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [line.transferAccountId, line.transferPairTransactionId]);

  const amountMinor = line.counterpartAmountMinor ?? pairAmountMinor;
  const title =
    line.transferPairTransactionId !== null && pairPostedOn !== null && amountMinor !== null
      ? t('transfer.paired', {
          date: formatCivilDate(pairPostedOn, locale),
          amount: formatMoneyMinor(amountMinor, operatingCurrency, locale),
        })
      : '';

  return (
    <Tooltip describeChild title={title}>
      <Chip
        size="small"
        label={t('transfer.chip', {
          account: line.transferAccountName ?? '',
        })}
        sx={{ color: 'text.secondary' }}
      />
    </Tooltip>
  );
}

function ReimbursementRegisterChip({
  line,
  link,
  peer,
  operatingCurrency,
  locale,
  onOpenPeer,
}: {
  line: OwnerRegisterLine;
  link: ReimbursementLink;
  peer: { payee: string; postedOn: string; amountMinor: number; accountId: number } | null;
  operatingCurrency: string | null;
  locale: string;
  onOpenPeer: (txId: number, accountId: number) => void;
}) {
  const { t } = useTranslation();
  const isCredit = link.creditTransactionId === line.id;
  const peerId = isCredit ? link.expenseTransactionId : link.creditTransactionId;
  const label =
    isCredit && peer !== null
      ? t('reimb.chipCredit', {
          payee: peer.payee,
          date: formatCivilDate(peer.postedOn, locale),
        })
      : !isCredit && peer !== null
        ? t('reimb.chipExpense', {
            amount: formatMoneyMinor(peer.amountMinor, operatingCurrency, locale),
          })
        : t('reimb.title');
  const tooltip =
    peer !== null
      ? `${peer.payee} · ${formatCivilDate(peer.postedOn, locale)} · ${formatMoneyMinor(peer.amountMinor, operatingCurrency, locale)}`
      : '';
  return (
    <Tooltip describeChild title={tooltip}>
      <Chip
        size="small"
        label={label}
        onClick={(event) => {
          event.stopPropagation();
          if (peer === null) return;
          onOpenPeer(peerId, peer.accountId);
        }}
      />
    </Tooltip>
  );
}
